import type { MeetingPageResponse } from '@wundo/shared/meetingPage.ts';
import {
  correctMeeting,
  deleteThreadDocument,
  type MeetingTemplateChoice,
} from '@/lib/meetingPage.ts';
import { MeetingAttendeesDialog } from './MeetingAttendeesDialog.tsx';
import type { MeetingAttendeeRow } from './meetingAttendeeList.ts';
import type {
  MeetingDialog,
  MeetingDialogKind,
  MeetingTemplatePick,
} from './meetingDialog.ts';
import { MeetingDocumentDeleteDialog } from './MeetingDocumentDeleteDialog.tsx';
import { MeetingRegenerateDialog } from './MeetingRegenerateDialog.tsx';
import { MeetingRemoveDialog } from './MeetingRemoveDialog.tsx';
import { MeetingShareNotesDialog } from './MeetingShareNotesDialog.tsx';
import { MeetingTemplateSwitchDialog } from './MeetingTemplateSwitchDialog.tsx';
import { MeetingTopicJudgmentDialog } from './MeetingTopicJudgmentDialog.tsx';
import { MeetingTranscriptDialog } from './MeetingTranscriptDialog.tsx';
import type { MeetingPanel } from './meetingPanel.ts';
import type { MeetingWrites } from './useMeetingWrites.ts';
import { ScopeDialog } from './ScopeDialog.tsx';
import { m } from '@/paraglide/messages.js';

type Correction = Pick<MeetingWrites['correction'], 'isPending' | 'isError' | 'reset'>;

export interface MeetingDialogsProps {
  dialog: MeetingDialog | null;
  closeDialog: (kinds: MeetingDialogKind[]) => void;
  page: MeetingPageResponse;
  userId: string;
  meetingId: string;
  attendees: MeetingAttendeeRow[];
  correction: Correction;
  generation: Pick<
    MeetingWrites['generation'],
    'isPending' | 'isError' | 'mutate' | 'reset'
  >;
  /* Whether the notes shared from the notetaker banner are being written up. */
  sharingNotes: boolean;
  run: (task: () => Promise<void>) => void;
  /* A gesprekstype pick moves on to its next question, or is saved. */
  onAdvanceTemplate: (pick: MeetingTemplatePick) => void;
  onSaveTemplate: (choice: MeetingTemplateChoice) => void;
  onShareNotes: (content: string) => void;
}

/** The questions and overlays of the meeting page, at most one at a time. */
export function MeetingDialogs({
  dialog,
  closeDialog,
  page,
  userId,
  meetingId,
  attendees,
  correction,
  generation,
  sharingNotes,
  run,
  onAdvanceTemplate,
  onSaveTemplate,
  onShareNotes,
}: MeetingDialogsProps) {
  const regenerating = dialog?.kind === 'regenerate' ? dialog.panel : null;

  return (
    <>
      <CorrectionDialogs
        dialog={dialog}
        closeDialog={closeDialog}
        page={page}
        userId={userId}
        meetingId={meetingId}
        correction={correction}
        run={run}
        onAdvanceTemplate={onAdvanceTemplate}
        onSaveTemplate={onSaveTemplate}
      />

      {/* A preparation keeps what the user wrote; a verslag keeps nothing. */}
      <MeetingRegenerateDialog
        open={regenerating !== null}
        title={regenerateTitle(regenerating)}
        body={regenerateBody(regenerating)}
        confirmLabel={regenerateConfirmLabel(regenerating)}
        failed={generation.isError}
        pending={generation.isPending}
        onConfirm={() =>
          regenerating && generation.mutate({ panel: regenerating, regenerate: true })
        }
        onCancel={() => {
          closeDialog(['regenerate']);
          generation.reset();
        }}
      />

      <MeetingAttendeesDialog
        open={dialog?.kind === 'attendees'}
        attendees={attendees}
        onClose={() => closeDialog(['attendees'])}
      />

      {/* Only offered while there is no note yet, so it always starts empty. */}
      {dialog?.kind === 'share-notes' && (
        <MeetingShareNotesDialog
          open
          pending={sharingNotes}
          initial={null}
          submitLabel={m.meeting_report_generate()}
          onSubmit={onShareNotes}
          onCancel={() => closeDialog(['share-notes'])}
        />
      )}

      <MeetingTranscriptDialog
        open={dialog?.kind === 'transcript'}
        userId={userId}
        meetingId={meetingId}
        onClose={() => closeDialog(['transcript'])}
      />
      <MeetingTopicJudgmentDialog
        open={dialog?.kind === 'topics'}
        userId={userId}
        meetingId={meetingId}
        onClose={() => closeDialog(['topics'])}
      />
    </>
  );
}

interface CorrectionDialogsProps {
  dialog: MeetingDialog | null;
  closeDialog: (kinds: MeetingDialogKind[]) => void;
  page: MeetingPageResponse;
  userId: string;
  meetingId: string;
  correction: Correction;
  run: (task: () => Promise<void>) => void;
  onAdvanceTemplate: (pick: MeetingTemplatePick) => void;
  onSaveTemplate: (choice: MeetingTemplateChoice) => void;
}

/* The questions a correction answers: one pending state, failure and cancel. */
function CorrectionDialogs({
  dialog,
  closeDialog,
  page,
  userId,
  meetingId,
  correction,
  run,
  onAdvanceTemplate,
  onSaveTemplate,
}: CorrectionDialogsProps) {
  const cancel = (kind: MeetingDialogKind) => {
    closeDialog([kind]);
    correction.reset();
  };
  const template = dialog?.kind === 'template' ? dialog : null;
  const deleting = dialog?.kind === 'delete-document' ? dialog.document : null;
  const thread = page.thread;

  return (
    <>
      <ScopeDialog
        open={dialog?.kind === 'notetaker'}
        thread={thread}
        kind={page.decision.kind}
        occurrenceOnly={page.decision.never_automatic}
        failed={correction.isError}
        pending={correction.isPending}
        onChoose={(scope) =>
          dialog?.kind === 'notetaker' &&
          run(() => correctMeeting(userId, meetingId, { record: dialog.record, scope }))
        }
        onCancel={() => cancel('notetaker')}
      />

      <MeetingRemoveDialog
        open={dialog?.kind === 'remove'}
        thread={thread}
        kind={page.decision.kind}
        occurrenceOnly={page.decision.never_automatic}
        failed={correction.isError}
        pending={correction.isPending}
        onChoose={(scope) =>
          run(() => correctMeeting(userId, meetingId, { follow: false, scope }))
        }
        onCancel={() => cancel('remove')}
      />

      {/* This meeting or the whole conversation, asked where the two differ. */}
      <ScopeDialog
        open={template !== null && template.scope === null}
        thread={thread}
        kind={page.decision.kind}
        occurrenceOnly={false}
        failed={correction.isError}
        pending={correction.isPending}
        onChoose={(scope) => template && onAdvanceTemplate({ ...template, scope })}
        onCancel={() => cancel('template')}
      />

      <MeetingTemplateSwitchDialog
        template={template?.scope ? template.template : null}
        failed={correction.isError}
        pending={correction.isPending}
        onChoose={(recreate) =>
          template &&
          onSaveTemplate({
            templateId: template.template.id,
            recreate,
            scope: template.scope ?? 'occurrence',
          })
        }
        onCancel={() => cancel('template')}
      />

      <MeetingDocumentDeleteDialog
        document={deleting}
        failed={correction.isError}
        pending={correction.isPending}
        onConfirm={() =>
          deleting &&
          thread &&
          run(() => deleteThreadDocument(userId, thread.id, deleting.id))
        }
        onCancel={() => cancel('delete-document')}
      />
    </>
  );
}

function regenerateTitle(panel: MeetingPanel | null): string {
  return panel === 'report'
    ? m.meeting_report_regenerate_title()
    : m.meeting_prep_regenerate_title();
}

function regenerateBody(panel: MeetingPanel | null): string {
  return panel === 'report'
    ? m.meeting_report_regenerate_body()
    : m.meeting_prep_regenerate_body();
}

function regenerateConfirmLabel(panel: MeetingPanel | null): string {
  return panel === 'report'
    ? m.meeting_report_regenerate_save()
    : m.meeting_prep_regenerate_save();
}
